import { useCallback } from 'react';

import {
  Divider,
  HStack,
  Text,
  VStack,
  Box,
  Flex,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorIndicator } from 'components/ErrorIndicator';
import { ArrowRightIcon, Card, CloudTransferIcon } from 'components/uikit';
import { OneXbetLogo } from 'components/uikit/icons';
import { chatwootIcon as ChatwootIcon } from 'components/uikit/icons/chatwoot';
import { RiobetLogo } from 'components/uikit/icons/RiobetLogo';
import { DOMAIN } from 'data/constants';
import { DepositBank, isDeeplinkMethod } from 'data/payments/models';
import { AnalyticsEvents, InputType, trackEvent } from 'utils/analytics';
import { deepLinks } from 'utils/bankDeepLinks';
import { BrowserInfo } from 'utils/browserInfo';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';

import { BankDeeplinkButtons } from './BankDeeplinkButtons';
import { BankDeeplinkQR, downloadQrAsPng } from './BankDeeplinkQR';
import { PaymentClientControlsDefault } from './PaymentClientControlsDefault';
import { PaymentCredentialsBaseProps } from './PaymentCredentials';
import { PaymentFieldRow } from './PaymentFieldRow';
import { PaymentInstructionsModal } from './PaymentInstructionsModal';
import { PaymentNotice } from './PaymentNotice';
import { PaymentStatusControls } from './PaymentStatusControls';
import { PaymentUploadModal } from './PaymentUploadModal';
import { PhpTgQrErrorBanner } from './PhpTgQrErrorBanner';
import { VndTgQrErrorBanner } from './VndTgQrErrorBanner';

interface PaymentCredentialsDefaultProps extends PaymentCredentialsBaseProps {
  style?: 'default' | 'riobet' | '1xbet';
  crossborderAmount: string | null;
  crossborderCurrency: string | null;
  onInputCopied: (inputName: InputType) => void;
  showChatwoot?: boolean;
}

export function PaymentCredentialsDefault({
  id,
  acquirer,
  method,
  amount,
  currency,
  credentials,
  failureRedirectLink,
  fromBank,
  status,
  timeLeft,
  paid,
  onPaid,
  onCancel,
  onInputCopied,
  onUploadClick,
  onUploadComplete,
  isUploadLoading,
  uploadModalIsOpen,
  uploadModalOnClose,
  style = 'default',
  crossborderAmount,
  crossborderCurrency,
  showChatwoot,
}: PaymentCredentialsDefaultProps) {
  const { t } = useTranslation('payments');
  const isRiobet = style === 'riobet';
  const isOneXbet = style === '1xbet';
  const isBranded = isRiobet || isOneXbet;
  const isMobile = useIsMobile();
  const isAndroid = isMobile && BrowserInfo.mobileOS === 'android';
  const instructionsDisclosure = useDisclosure();

  const hasDeeplinks = (() => {
    if (!method || !fromBank || !isMobile) return false;
    const mobileOS = BrowserInfo.mobileOS;
    if (!mobileOS) return false;
    const bank = fromBank as DepositBank;
    const bankLinks = deepLinks[mobileOS]?.[bank]?.[method];
    return !!bankLinks && Object.keys(bankLinks).length > 0;
  })();

  const credentialLabel = t(
    credentials.type === 'SBP' ? 'payment_phone_number' : 'payment_card_number',
  );

  const isVietnam = method === 'VND_TG_QR';
  const isPhilippines = method === 'PHP_TG_QR';
  const isSimpleLayout =
    fromBank === 'ВТБ' || fromBank === 'МТС Банк' || fromBank === 'Газпромбанк';

  const qrUrlPrefix = isVietnam ? 'vnd' : isPhilippines ? 'php' : '';

  const instructionsPrefix = 'vnd';

  const QR_BANK_CONFIG: Record<
    string,
    {
      payImageUrl: string;
      payLink: string;
      alt: string;
      fileInstructionImageUrl: string;
      buttonNameKey: string;
      qrImageUrl: string;
    }
  > = {
    Сбербанк: {
      payImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber-pay.png`,
      payLink: 'https://www.sberbank.ru/sms/qr',
      alt: 'Sber Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_sber`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/qr.png`,
    },
    ВТБ: {
      payImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/vtb-pay.png`,
      payLink: 'https://online.vtb.ru/i/qrmain',
      alt: 'VTB Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/vtb/file_instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_vtb`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/sber/qr.png`,
    },
    'МТС Банк': {
      payImageUrl: `https://payment.${DOMAIN}/assets/vnd/mts-pay.png`,
      payLink: 'mymts://action/oplata_qr',
      alt: 'MTS Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/vnd/mts/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_mts`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/vnd/sber/qr.png`,
    },
    Газпромбанк: {
      payImageUrl: `https://payment.${DOMAIN}/assets/vnd/gazprom-pay.png`,
      payLink: 'gpbapp://payments/qrcode',
      alt: 'Gazprombank Pay',
      fileInstructionImageUrl: `https://payment.${DOMAIN}/assets/vnd/gazprom/file-instruction.png`,
      buttonNameKey: `${qrUrlPrefix}_button_name_gazprom`,
      qrImageUrl: `https://payment.${DOMAIN}/assets/vnd/sber/qr.png`,
    },
  };

  const bankConfig = fromBank ? QR_BANK_CONFIG[fromBank] : undefined;

  const openBankPayLink = useCallback(() => {
    if (!bankConfig?.payLink) return;
    trackEvent(AnalyticsEvents.PAYMENT_BANK_PAY_LINK_CLICKED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      bank: fromBank ?? undefined,
    });
    window.open(bankConfig.payLink, '_blank');
  }, [bankConfig, id, method, amount, fromBank]);

  const qrErrorImageUrl = `https://payment.${DOMAIN}/assets/${qrUrlPrefix}/errorImage.png`;

  const handleSaveQr = useCallback(() => {
    if (!credentials.value) return;
    trackEvent(AnalyticsEvents.PAYMENT_QR_SAVED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      bank: fromBank ?? undefined,
    });
    const params = isAndroid
      ? {
          canvasWidth: 500,
          canvasHeight: 1000,
          qrWidthRatio: 0.7,
          qrHeightRatio: 0.7,
          offsetXRatio: 0.17,
          offsetYRatio: 0.42,
        }
      : {
          canvasWidth: 1000,
          canvasHeight: 1000,
          qrWidthRatio: 0.25,
          qrHeightRatio: 0.25,
          offsetXRatio: 0.07,
          offsetYRatio: 0.3,
        };
    downloadQrAsPng(
      credentials.value,
      `qr-${id}.png`,
      method ?? 'VND_TG_QR',
      params,
    );
  }, [credentials.value, id, method, amount, fromBank, isAndroid]);

  const renderControls = () => {
    if (acquirer === 'TEST') return <PaymentStatusControls id={id} />;
    return (
      <PaymentClientControlsDefault
        id={id}
        failureRedirectLink={failureRedirectLink}
        onPaid={onPaid}
        onCancel={onCancel}
      />
    );
  };

  const content = (
    <VStack
      spacing={['0', '24px']}
      align='start'
      w='100%'
      pt={['0', '0']}
      zIndex={1}
    >
      {/* Brand Logo */}
      {isRiobet ? (
        <Box
          w='100%'
          display='flex'
          justifyContent='center'
          pt={['20px', '0']}
          pb={['20px', '30px']}
          flexShrink={0}
        >
          <RiobetLogo w='175px' h='56px' />
        </Box>
      ) : isOneXbet ? (
        <HStack
          bgColor='#1C4268'
          alignItems='center'
          justifyContent='center'
          w={['100vw', '100%']}
          maxW={['none', isVietnam || isPhilippines ? '550px' : '900px']}
          mx={['calc(-50vw + 50%)', '0']}
          py={['16px', '20px']}
          borderRadius={['0', '8px']}
        >
          <OneXbetLogo
            color='white'
            w={['80px', '100px']}
            h={['18px', '22px']}
          />
        </HStack>
      ) : null}
      <Card
        bg={['transparent', 'grey.white']}
        boxShadow='none'
        p={['20px', '20px']}
        w={['100vw', '100%']}
        h={['auto', 'auto']}
        maxW={['none', isVietnam || isPhilippines ? '550px' : '900px']}
        borderWidth={['0', '1px']}
        borderColor={['transparent', 'grey.border']}
        borderRadius={['0', '8px']}
        mx={['calc(-50vw + 50%)', '0']}
        my={['0', '0']}
        pb={['20px', '20px']}
        backdropFilter='blur(14px)'
        position='relative'
      >
        {(isVietnam || isPhilippines) && !isMobile && bankConfig ? (
          <Box
            as='img'
            src={bankConfig.payImageUrl}
            alt={bankConfig.alt}
            position='absolute'
            top='12px'
            left='12px'
            w='80px'
            h='auto'
            zIndex={10}
          />
        ) : null}
        {isVietnam || isPhilippines ? (
          <VStack
            w='100%'
            align='stretch'
            spacing={['16px', '0']}
            justify={['space-between', 'space-between']}
            minH={['auto', '100%']}
          >
            <Box>
              <VStack
                pt={['0', '12px']}
                gap={['0px', '0px']}
                w='100%'
                mb={['16px', '20px']}
              >
                <Text
                  as='h3'
                  fontSize={['18px', '20px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='payment.titleText'
                  lineHeight='1'
                >
                  {t('payment_credentials_title')}
                </Text>

                <Text
                  as='h1'
                  fontSize={['56px', '64px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='grey.primaryText'
                  lineHeight='1'
                >
                  {amount}
                  <sub style={{ bottom: 0 }}>{currency}</sub>
                </Text>
                <Text
                  fontWeight='200'
                  fontSize='md'
                  color='payment.titleText'
                  lineHeight='1'
                >
                  {t(`${instructionsPrefix}_tg_qr_comission_warning`)}
                </Text>
              </VStack>

              {credentials.type === 'QR' && (
                <Box
                  w='100%'
                  h='auto'
                  p={[isBranded ? '12px' : '0px', '20px']}
                  bg={['transparent', 'payment.qrBackground']}
                  borderWidth={isBranded ? '1px' : '0'}
                  borderColor='payment.qrBorder'
                  borderRadius='13px'
                  mx='auto'
                  mb={'20px'}
                  position='relative'
                >
                  <Flex
                    flexDirection={['column', 'row']}
                    justifyContent={['center', 'space-between']}
                    alignItems={['center', 'center']}
                    textAlign='center'
                    gap={['20px', '30px']}
                  >
                    {isMobile ? (
                      <>
                        {hasDeeplinks ? (
                          <BankDeeplinkButtons
                            amount={amount}
                            fromBank={fromBank ?? null}
                            method={method}
                            credentials={credentials}
                            id={id}
                          />
                        ) : null}
                        <Box w='100%' display='flex' justifyContent='center'>
                          <BankDeeplinkQR
                            id={id}
                            fromBank={fromBank ?? null}
                            method={method}
                            value={credentials.value}
                          />
                        </Box>
                        <Card
                          w='100%'
                          bg={
                            isBranded
                              ? 'transparent'
                              : 'rgba(255, 255, 255, 0.5)'
                          }
                          p='16px'
                          borderRadius='12px'
                          boxShadow='none'
                          borderWidth={isBranded ? '0' : '1px'}
                          borderColor='gray.300'
                        >
                          <VStack
                            textAlign='start'
                            alignItems='stretch'
                            w='100%'
                            spacing='8px'
                          >
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='12px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                1.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={
                                    isAndroid
                                      ? `payment_guide_${instructionsPrefix}_step_1_mobile_v2`
                                      : `payment_guide_${instructionsPrefix}_step_1_mobile`
                                  }
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              {isAndroid ? (
                                <Button
                                  onClick={handleSaveQr}
                                  variant='paymentOutline'
                                  h='36px'
                                  px='14px'
                                  borderRadius='8px'
                                  fontSize='sm'
                                  fontWeight='600'
                                  flexShrink={0}
                                >
                                  {t(
                                    `payment_guide_${instructionsPrefix}_save_qr`,
                                  )}
                                </Button>
                              ) : null}
                            </HStack>
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='10px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                2.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={`payment_guide_${instructionsPrefix}_step_2_mobile_v2`}
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              <ArrowRightIcon
                                color='grey.primaryText'
                                boxSize='20px'
                                flexShrink={0}
                              />
                              {bankConfig ? (
                                <Button
                                  onClick={openBankPayLink}
                                  variant='unstyled'
                                  p='0'
                                  h='auto'
                                  w='auto'
                                  minW='auto'
                                  display='flex'
                                  alignItems='center'
                                  flexShrink={0}
                                  _hover={{ opacity: 0.8 }}
                                  _active={{ opacity: 0.6 }}
                                  _focus={{ boxShadow: 'none' }}
                                >
                                  <Box
                                    as='img'
                                    src={bankConfig.payImageUrl}
                                    alt={bankConfig.alt}
                                    w='70px'
                                    h='auto'
                                  />
                                </Button>
                              ) : null}
                            </HStack>
                            <HStack
                              w='100%'
                              justifyContent='space-between'
                              alignItems='center'
                              gap='10px'
                            >
                              <Text
                                fontWeight='500'
                                fontSize='md'
                                color='grey.primaryText'
                                lineHeight='1.4'
                                flex='1'
                              >
                                3.{' '}
                                <Trans
                                  ns='payments'
                                  i18nKey={
                                    isAndroid
                                      ? `payment_guide_${instructionsPrefix}_step_3_mobile_v2`
                                      : `payment_guide_${instructionsPrefix}_step_3_mobile`
                                  }
                                  values={{
                                    buttonName: bankConfig
                                      ? t(bankConfig.buttonNameKey)
                                      : '',
                                  }}
                                  components={{
                                    bold: <Text as='span' fontWeight='700' />,
                                  }}
                                />
                              </Text>
                              {bankConfig ? (
                                <Box
                                  as='img'
                                  src={bankConfig.fileInstructionImageUrl}
                                  alt='File instruction'
                                  w='120px'
                                  h='auto'
                                  flexShrink={0}
                                />
                              ) : null}
                            </HStack>
                            {isVietnam && fromBank === 'Сбербанк' ? (
                              <HStack
                                w='100%'
                                justifyContent='space-between'
                                alignItems='center'
                                gap='10px'
                              >
                                <Text
                                  fontWeight='500'
                                  fontSize='md'
                                  color='grey.primaryText'
                                  lineHeight='1.4'
                                  flex='1'
                                >
                                  4.{' '}
                                  <Trans
                                    ns='payments'
                                    i18nKey={`payment_guide_${instructionsPrefix}_step_4_mobile_v3`}
                                    components={{
                                      bold: <Text as='span' fontWeight='700' />,
                                    }}
                                  />
                                </Text>
                                <ArrowRightIcon
                                  color='grey.primaryText'
                                  boxSize='20px'
                                  flexShrink={0}
                                />
                                <Box
                                  as='img'
                                  src={qrErrorImageUrl}
                                  alt='Error illustration'
                                  w='90px'
                                  h='auto'
                                  borderRadius='8px'
                                  flexShrink={0}
                                />
                              </HStack>
                            ) : null}
                          </VStack>
                        </Card>
                      </>
                    ) : (
                      <>
                        <VStack
                          textAlign='start'
                          alignItems='start'
                          w={['calc(100% - 120px)', '70%']}
                          maxW={['none', 'none']}
                          flex='1'
                          spacing={['8px', '0']}
                          gap={['8px', '0']}
                        >
                          <Text
                            fontWeight='400'
                            fontSize={['md', 'xl']}
                            color='grey.primaryText'
                            lineHeight='1.4'
                          >
                            1.{' '}
                            <Trans
                              ns='payments'
                              i18nKey={`payment_guide_${instructionsPrefix}_step_1_desktop`}
                              values={{
                                bank: fromBank
                                  ? t(`bank_name_${fromBank}`)
                                  : '',
                              }}
                              components={{
                                bold: <Text as='span' fontWeight='900' />,
                              }}
                            />
                          </Text>
                          <HStack w='100%' gap='15px' position='relative'>
                            <Text
                              fontWeight='400'
                              fontSize={['md', 'xl']}
                              color='grey.primaryText'
                              lineHeight='1.4'
                            >
                              2.{' '}
                              <Trans
                                ns='payments'
                                i18nKey={
                                  fromBank === 'ВТБ'
                                    ? `payment_guide_${instructionsPrefix}_vtb_step_2_desktop`
                                    : fromBank === 'МТС Банк'
                                    ? `payment_guide_${instructionsPrefix}_mts_step_2_desktop`
                                    : `payment_guide_${instructionsPrefix}_step_2_desktop`
                                }
                                components={{
                                  bold: <Text as='span' fontWeight='900' />,
                                }}
                              />
                            </Text>
                            <Box
                              position='absolute'
                              left='calc(90% + 2px)'
                              top='50%'
                              transform='translateY(-50%)'
                              w='24px'
                              h='24px'
                              pointerEvents='none'
                              display={
                                (isVietnam || isPhilippines) &&
                                !isMobile &&
                                !isSimpleLayout
                                  ? 'block'
                                  : 'none'
                              }
                            >
                              <ArrowRightIcon
                                color='grey.primaryText'
                                boxSize='32px'
                              />
                            </Box>
                          </HStack>
                          <Box mb='10px'>
                            <Text
                              fontWeight='400'
                              fontSize={['md', 'xl']}
                              color='grey.primaryText'
                              lineHeight='1.4'
                            >
                              3.{' '}
                              <Trans
                                ns='payments'
                                i18nKey={`payment_guide_${instructionsPrefix}_step_3_desktop`}
                                components={{
                                  bold: <Text as='span' fontWeight='900' />,
                                }}
                              />
                            </Text>
                          </Box>
                          <Text
                            fontWeight='400'
                            fontSize={['md', 'xl']}
                            color='grey.primaryText'
                            lineHeight='1.4'
                            mt={['16px', '24px']}
                          >
                            4.{' '}
                            <Trans
                              ns='payments'
                              i18nKey={`payment_guide_${instructionsPrefix}_step_4_desktop`}
                              components={{
                                bold: <Text as='span' fontWeight='900' />,
                              }}
                            />
                          </Text>
                        </VStack>
                        <Box
                          flexShrink={0}
                          w={[
                            '100px',
                            isSimpleLayout && !isMobile ? '180px' : '245px',
                          ]}
                          maxW='100%'
                          position='relative'
                        >
                          {!isSimpleLayout || isMobile ? (
                            <Box
                              as='img'
                              src={bankConfig?.qrImageUrl}
                              alt='QR frame'
                              w='100%'
                              h='auto'
                              display='block'
                              userSelect='none'
                            />
                          ) : null}
                          <Box
                            position={
                              isSimpleLayout && !isMobile
                                ? 'relative'
                                : 'absolute'
                            }
                            // NOTE: tune these values if the template image changes.
                            top={isSimpleLayout && !isMobile ? 'auto' : '11%'}
                            left={isSimpleLayout && !isMobile ? 'auto' : '65%'}
                            transform={
                              isSimpleLayout && !isMobile
                                ? 'none'
                                : 'translateX(-50%)'
                            }
                            zIndex={1}
                            display='flex'
                            justifyContent='center'
                            alignItems='center'
                          >
                            <BankDeeplinkQR
                              id={id}
                              fromBank={fromBank ?? null}
                              method={method}
                              value={credentials.value}
                              size={isSimpleLayout && !isMobile ? 180 : 155}
                            />
                          </Box>
                        </Box>
                      </>
                    )}
                  </Flex>
                  {!isMobile ? (
                    <>
                      <Divider
                        borderColor='grey.border'
                        h='1px'
                        my='10px'
                        w='100%'
                      />
                      {isVietnam && fromBank === 'Сбербанк' ? (
                        <VndTgQrErrorBanner
                          hasDeeplink={hasDeeplinks}
                          isMobile={isMobile}
                          fromBank={fromBank ?? null}
                        />
                      ) : isPhilippines ? (
                        <PhpTgQrErrorBanner
                          hasDeeplink={hasDeeplinks}
                          isMobile={isMobile}
                          fromBank={fromBank ?? null}
                        />
                      ) : null}
                    </>
                  ) : null}
                </Box>
              )}

              {showChatwoot ? (
                <HStack
                  w='100%'
                  bg='payment.qrBackground'
                  borderWidth='1px'
                  borderColor='payment.qrBorder'
                  borderRadius='999px'
                  p='10px 16px'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                  mb='12px'
                  display={['flex', 'none']}
                >
                  <Text
                    fontSize='sm'
                    fontWeight='semibold'
                    color={isBranded ? 'white' : 'payment.titleText'}
                  >
                    {t('payment_chat_hint')}
                  </Text>
                  <ChatwootIcon boxSize='18px' flexShrink={0} />
                </HStack>
              ) : null}
              <HStack
                justifyContent='space-between'
                alignItems='center'
                mb='5px'
              >
                <Button
                  variant='paymentOutline'
                  borderRadius='8px'
                  justifyContent='center'
                  width='80%'
                  display={['block', 'block']}
                  onClick={instructionsDisclosure.onOpen}
                  fontWeight='semibold'
                  fontSize={['sm', 'md']}
                >
                  {t('payment_instructions')}
                </Button>
                <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px'>
                  {!(paid || status === 'PAID') && (
                    <Text
                      fontSize={['lg', 'xl']}
                      color='payment.timeBarText'
                      fontWeight='medium'
                    >
                      {timeLeft}
                    </Text>
                  )}
                </Box>
              </HStack>

              <VStack
                gap='0'
                spacing={0}
                w='100%'
                align='stretch'
                mb={['0', '0']}
              >
                <PaymentFieldRow
                  label={t('payment_amount')}
                  value={`${crossborderAmount ?? ''} ${
                    crossborderCurrency ?? ''
                  }`}
                  onCopyFunc={() => onInputCopied('amount')}
                />
              </VStack>
            </Box>
            {showChatwoot ? (
              <HStack
                w='100%'
                bg='payment.qrBackground'
                borderWidth='1px'
                borderColor='payment.qrBorder'
                borderRadius='8px'
                p='10px 16px'
                justifyContent='center'
                alignItems='center'
                gap='8px'
                display={['none', 'flex']}
              >
                <Text
                  fontSize='sm'
                  fontWeight='semibold'
                  color={isBranded ? 'white' : 'payment.titleText'}
                >
                  {t('payment_chat_hint')}
                </Text>
                <ChatwootIcon boxSize='18px' flexShrink={0} />
              </HStack>
            ) : null}
            {/* Controls */}
            <Box bottom='0' mb='0' w='100%' pt={['12px', '15px']}>
              {renderControls()}
            </Box>
          </VStack>
        ) : (
          <HStack
            align='stretch'
            spacing='0'
            gap={['20px', '10px']}
            w='100%'
            flexDir={['column', 'row']}
          >
            {/* Left section: Header with amount, QR, and button */}
            <VStack
              align={['start', 'stretch']}
              alignSelf={['stretch', 'stretch']}
              justify={['flex-start', 'space-between']}
              flexShrink={0}
              w={['100%', 'auto']}
              spacing={['20px', credentials.type === 'QR' ? '20px' : '16px']}
            >
              <VStack pt={['0', '12px']} gap={['12px', '8px']} w='100%'>
                {isMobile ? null : (
                  <Text
                    as='h3'
                    fontSize={['20px', '20px']}
                    fontWeight='medium'
                    variant='numeric'
                    color='payment.titleText'
                    lineHeight='1'
                  >
                    {t('payment_credentials_title')}
                  </Text>
                )}
                <Text
                  as='h1'
                  fontSize={['72px', '64px']}
                  fontWeight='medium'
                  variant='numeric'
                  color='grey.primaryText'
                  lineHeight='1'
                >
                  {amount}
                  <sub style={{ bottom: 0 }}>{currency}</sub>
                </Text>
              </VStack>
              {credentials.type === 'QR' || !isMobile ? (
                <VStack textAlign='center' w={['100%', 'auto']}>
                  {credentials.type === 'QR' ? (
                    <>
                      <Flex
                        w={['180px', '200px']}
                        h={['180px', '200px']}
                        bg={['transparent', 'payment.qrBackground']}
                        borderWidth={isBranded ? '1px' : '0'}
                        borderColor='payment.qrBorder'
                        borderRadius='13px'
                        justifyContent='center'
                        alignItems='center'
                        mx={['auto', '0']}
                      >
                        <BankDeeplinkQR
                          id={id}
                          fromBank={fromBank ?? null}
                          method={method}
                          value={credentials.value}
                        />
                      </Flex>
                      {!(isVietnam || isPhilippines) && (
                        <Text
                          fontSize='md'
                          fontWeight='medium'
                          color='grey.tertiaryText'
                          w={['100%', '360px']}
                          display='block'
                        >
                          {t('pay_qr')}
                        </Text>
                      )}
                    </>
                  ) : method &&
                    isDeeplinkMethod(method) &&
                    method !== 'MOBCOM' ? (
                    <>
                      <Flex
                        w={['180px', '200px']}
                        h={['180px', '200px']}
                        bg={['transparent', 'payment.qrBackground']}
                        borderWidth={isBranded ? '1px' : '0'}
                        borderColor='payment.qrBorder'
                        borderRadius='13px'
                        justifyContent='center'
                        alignItems='center'
                        mx={['auto', '0']}
                      >
                        <BankDeeplinkQR
                          id={id}
                          fromBank={fromBank ?? null}
                          method={method}
                        />
                      </Flex>
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t('pay_qr')}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Flex
                        w={['180px', '200px']}
                        h={['180px', '200px']}
                        bg={['transparent', 'payment.qrBackground']}
                        borderRadius='13px'
                        justifyContent='center'
                        alignItems='center'
                        mx={['auto', '0']}
                      >
                        <CloudTransferIcon
                          cloudColor='payment.cloudColor'
                          arrowColor='payment.arrowColor'
                        />
                      </Flex>
                      <Text
                        fontSize='md'
                        fontWeight='medium'
                        color='grey.tertiaryText'
                        w={['100%', '360px']}
                        display='block'
                      >
                        {t('pay_transfer')}
                      </Text>
                    </>
                  )}
                </VStack>
              ) : null}
              <Button
                w='100%'
                h='40px'
                variant='paymentOutline'
                borderRadius='8px'
                justifyContent='center'
                display={['none', 'block']}
                onClick={instructionsDisclosure.onOpen}
              >
                {t('payment_instructions')}
              </Button>
              {/* Deep link buttons - shown on mobile for AMD_TG_SBP, hidden on desktop, but not shown when credentials.type === 'QR' */}
              {isMobile &&
                method &&
                isDeeplinkMethod(method) &&
                credentials.type !== 'QR' && (
                  <Box w='100%'>
                    <BankDeeplinkButtons
                      amount={amount}
                      fromBank={fromBank ?? null}
                      method={method}
                      credentials={credentials}
                      id={id}
                    />
                  </Box>
                )}
            </VStack>

            {/* Right section: Payment details */}
            <VStack
              w='100%'
              align='stretch'
              px={['0', '10px']}
              spacing={['20px', '15px']}
              justify={['space-between', 'space-between']}
              minH={['auto', '100%']}
            >
              <Box>
                <HStack justifyContent='space-between' alignItems='center'>
                  <Text fontWeight='semibold' fontSize='xl'>
                    {t(
                      method === 'MOBCOM'
                        ? 'payment_details_mobcom'
                        : 'payment_details',
                    )}
                  </Text>
                  <Box bg='payment.timeBar' borderRadius='13px' p='10px 20px'>
                    {!(paid || status === 'PAID') && (
                      <Text
                        fontSize='xl'
                        color='payment.timeBarText'
                        fontWeight='medium'
                      >
                        {timeLeft}
                      </Text>
                    )}
                  </Box>
                </HStack>

                <VStack
                  gap='0'
                  spacing={0}
                  w='100%'
                  align='stretch'
                  mb={['0', '0']}
                >
                  <PaymentFieldRow
                    label={t('payment_amount')}
                    value={`${crossborderAmount ?? amount} ${
                      crossborderCurrency ?? currency
                    }`}
                    onCopyFunc={() => onInputCopied('amount')}
                  />
                  {credentials.type !== 'QR' ? (
                    <>
                      <Divider />
                      <PaymentFieldRow
                        label={credentialLabel}
                        value={credentials.value ?? ''}
                        customClass='ph-no-capture'
                        onCopyFunc={() => onInputCopied('credentials')}
                      />
                    </>
                  ) : null}
                  {credentials.bank && !(isVietnam || isPhilippines) && (
                    <>
                      <Divider />
                      <PaymentFieldRow
                        label={t(
                          method === 'MOBCOM'
                            ? 'payment_operator'
                            : 'payment_bank',
                        )}
                        value={credentials.bank}
                        onCopyFunc={() => onInputCopied('bank')}
                      />
                    </>
                  )}
                  {credentials.name && (
                    <>
                      <Divider />
                      <PaymentFieldRow
                        label={t('payment_recipient')}
                        value={credentials.name}
                        onCopyFunc={() => onInputCopied('recipient')}
                      />
                    </>
                  )}
                  {method === 'SBER_AZN' && (
                    <>
                      <Divider />
                      <PaymentFieldRow
                        label={t('payment_sender_sber')}
                        value=''
                        displayedValue={<ErrorIndicator />}
                        showCopy={false}
                      />
                    </>
                  )}
                </VStack>

                {/* Payment instructions button - shown on mobile, hidden on desktop */}
                <Button
                  w='100%'
                  h='40px'
                  variant='paymentOutline'
                  borderRadius='8px'
                  justifyContent='center'
                  display={['block', 'none']}
                  onClick={instructionsDisclosure.onOpen}
                  mt={['20px', '0']}
                  mb={[method === 'MOBCOM' ? '0' : '20px', '0']}
                >
                  {t('payment_instructions')}
                </Button>

                {/* Payment notice */}
                <Box>
                  <PaymentNotice
                    method={method}
                    fromBank={fromBank}
                    credentials={credentials}
                    style={style}
                  />
                </Box>
              </Box>
              {showChatwoot ? (
                <HStack
                  w='100%'
                  bg='payment.qrBackground'
                  borderWidth='1px'
                  borderColor='payment.qrBorder'
                  borderRadius='8px'
                  p='10px 16px'
                  justifyContent='center'
                  alignItems='center'
                  gap='8px'
                >
                  <Text
                    fontSize='sm'
                    fontWeight='semibold'
                    color={isBranded ? 'white' : 'payment.titleText'}
                  >
                    {t('payment_chat_hint')}
                  </Text>
                  <ChatwootIcon boxSize='18px' flexShrink={0} />
                </HStack>
              ) : null}
              {/* Controls */}
              <Box
                bottom='0'
                mb='0'
                w='100%'
                pt={method === 'MOBCOM' ? '0' : '15px'}
              >
                {renderControls()}
              </Box>
            </VStack>
          </HStack>
        )}
      </Card>
      <PaymentUploadModal
        isOpen={uploadModalIsOpen}
        onClose={uploadModalOnClose}
        onUploadClick={onUploadClick}
        isLoading={isUploadLoading}
        onUpload={onUploadComplete}
      />
      <PaymentInstructionsModal
        isOpen={instructionsDisclosure.isOpen}
        onClose={instructionsDisclosure.onClose}
        depositId={id}
        method={method}
        credentials={credentials}
        fromBank={fromBank}
        amount={crossborderAmount ?? amount}
        currency={crossborderCurrency ?? currency}
      />
    </VStack>
  );

  return content;
}
